import React, { useState, useEffect, useCallback, useRef } from 'react';
import { clientsApi, Client } from '../api/clients';

interface ClientSearchProps {
  onSelect: (client: Client | null) => void;
  selectedClient: Client | null;
  disabled?: boolean;
}

const ClientSearch: React.FC<ClientSearchProps> = ({
  onSelect,
  selectedClient,
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function
  const performSearch = useCallback(async (term: string) => {
    if (!term.trim() || term.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await clientsApi.getClients(1, 10, term);
      setResults(response.clients);
      setShowDropdown(true); // Show dropdown for both results and "no results" message
    } catch (err: any) {
      console.error('Error searching clients:', err);
      setError(err.response?.data?.error || 'Failed to search clients');
      setResults([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change with debouncing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer (300ms)
    debounceTimerRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  // Handle client selection
  const handleSelectClient = (client: Client) => {
    onSelect(client);
    setSearchTerm('');
    setShowDropdown(false);
    setResults([]);
  };

  // Handle clear selection
  const handleClear = () => {
    onSelect(null);
    setSearchTerm('');
    setResults([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // If a client is selected, show their info
  if (selectedClient) {
    return (
      <div className="client-search-selected">
        <div className="selected-client-info">
          <div className="selected-client-name">
            {selectedClient.firstName} {selectedClient.lastName}
          </div>
          <div className="selected-client-email">{selectedClient.email}</div>
          {selectedClient.phoneNumber && (
            <div className="selected-client-phone">{selectedClient.phoneNumber}</div>
          )}
        </div>
        <button
          type="button"
          className="clear-selection-btn"
          onClick={handleClear}
          disabled={disabled}
        >
          Clear
        </button>
      </div>
    );
  }

  return (
    <div className="client-search" ref={dropdownRef}>
      <div className="client-search-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="client-search-input"
          placeholder="Search by name, email, or phone..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => searchTerm.length >= 2 && results.length > 0 && setShowDropdown(true)}
          disabled={disabled}
        />
        {isLoading && <span className="search-loading-indicator">Loading...</span>}
      </div>

      {error && <div className="client-search-error">{error}</div>}

      {showDropdown && results.length > 0 && (
        <ul className="client-search-dropdown">
          {results.map((client) => (
            <li
              key={client.id}
              className="client-search-item"
              onClick={() => handleSelectClient(client)}
            >
              <div className="client-item-name">
                {client.firstName} {client.lastName}
              </div>
              <div className="client-item-email">{client.email}</div>
            </li>
          ))}
        </ul>
      )}

      {showDropdown && searchTerm.length >= 2 && results.length === 0 && !isLoading && (
        <div className="client-search-no-results">No clients found</div>
      )}
    </div>
  );
};

export default ClientSearch;
